import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "next/link";
import Image from "next/image";
import type {CSSProperties} from "react";
import {SOS_SITE_ID} from "../../../pages/config/settings";
import useWindowSize from "../../../utility/useWindowSize";
import {dataLayerClickLogo} from "tracking/events/navigation";

interface Props {
    navigation: Navigation;
    portalHomeLink?: string;
}

interface Navigation {
    content: {
        site: {
            id: string;
            root: NavigationRoot;
        };
    };
}

interface NavigationRoot {
    selfUrl: string;
    name: string;
    desktopLogo?: NavigationLogo | null;
    mobileLogo?: NavigationLogo | null;
}

interface NavigationLogo {
    data?: {
        uri?: string | null;
    } | null;
    alt?: string | null;
}

interface LogoPicture {
    uri: string;
    alt: string;
    style: CSSProperties;
    width: number;
    height: number;
}

interface LogoDimension {
    width: number;
    height: number;
}

const PORTAL_WIDTH = 300;
const PORTAL_HEIGHT = 44;
const PORTAL_HEIGHT_SMALL = 24; // isPortalLogo && lowerThanWindowSmall

const XL_WIDTH = 300;
const XL_HEIGHT = 40;

const LG_WIDTH = 200;
const LG_HEIGHT = 24;

const SM_WIDTH = 200;
const SM_HEIGHT = 40;

const DEFAULT_WIDTH = 300;
const DEFAULT_HEIGHT = 35;

function computeLogoDimensions(
    isPortalLogo: boolean,
    opts: {
        lowerThanWindowSmall: boolean;
        greaterThanWindowExtraLarge: boolean;
        greaterThanWindowLarge: boolean
    }
): LogoDimension {
    const {lowerThanWindowSmall, greaterThanWindowExtraLarge, greaterThanWindowLarge} = opts;

    // Priority: Portal > XL > LG > Small > Default
    if (isPortalLogo) {
        return {
            width: PORTAL_WIDTH,
            height: lowerThanWindowSmall ? PORTAL_HEIGHT_SMALL : PORTAL_HEIGHT,
        };
    }

    if (greaterThanWindowExtraLarge) {
        return {width: XL_WIDTH, height: XL_HEIGHT};
    }

    if (greaterThanWindowLarge) {
        return {width: LG_WIDTH, height: LG_HEIGHT};
    }

    if (lowerThanWindowSmall) {
        return {width: SM_WIDTH, height: SM_HEIGHT};
    }

    return {width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT};
}

function createLogoPicture(
    logo: NavigationLogo | null | undefined,
    style: CSSProperties,
    dimensions: LogoDimension
): LogoPicture | null {
    if (!logo?.data?.uri) {
        return null;
    }

    return {
        uri: logo.data.uri,
        alt: logo.alt ?? '',
        style,
        ...dimensions,
    };
}

function Logo({navigation, portalHomeLink}: Props) {
    const {
        lowerThanWindowSmall,
        lowerThanWindowMedium,
        greaterThanWindowExtraLarge,
        greaterThanWindowLarge
    } = useWindowSize()

    const logoStyle: CSSProperties = {
        width: 'auto',
        marginTop: '4px',
        marginLeft: lowerThanWindowMedium ? '8px' : '0px',
    }

    const navigationRoot = navigation.content.site.root;
    const isPortalLogo = navigation.content.site.id === SOS_SITE_ID;

    const dimensions = computeLogoDimensions(isPortalLogo, {
        lowerThanWindowSmall,
        greaterThanWindowExtraLarge,
        greaterThanWindowLarge,
    });
    const desktopLogoPicture = createLogoPicture(navigationRoot.desktopLogo, logoStyle, dimensions);
    const mobileLogoPicture = createLogoPicture(navigationRoot.mobileLogo, logoStyle, dimensions) ??
                              desktopLogoPicture;
    const logoPicture = lowerThanWindowSmall ? mobileLogoPicture : desktopLogoPicture;
    const logoLink = portalHomeLink || navigationRoot.selfUrl || "#";

    return <Box sx={{display: 'flex', justifyContent: 'flex-start', flexGrow: 1}}>
        <Link id={"logo"} href={logoLink} prefetch={false} onClick={() => dataLayerClickLogo()}>
            {logoPicture
                ? <Image data-cypress={'logoImage'}
                         alt={logoPicture.alt}
                         src={`/media${logoPicture.uri}`}
                         priority={true}
                         width={logoPicture.width}
                         height={logoPicture.height}
                         style={logoPicture.style}
                />
                : <Typography>{navigationRoot.name}</Typography>}
        </Link>
    </Box>

}

export default Logo;